// src/components/LandingPage/LandingPage.tsx

import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import api from "../../api/client";

import {
  setUserEmail,
  setUserToken,
  setAgencyName,
} from "../../redux/userSlice";

import "./LandingPage.css";

const LandingPage: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [agencyName, setAgencyNameInput] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isSignUp, setIsSignUp] = useState<boolean>(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ============================================================
     SIGN IN
  ============================================================ */

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    try {
      const response = await api.post("/signin", {
        email,
        password,
      });

      // Store logged-in user information in Redux
      dispatch(setUserEmail(email));

      if (response.data.token) {
        dispatch(setUserToken(response.data.token));
      }

      if (response.data.agency_name) {
        dispatch(setAgencyName(response.data.agency_name));
      }

      navigate("/home");
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Something went wrong";

      setErrorMessage(msg);

      console.error("Sign in failed:", err);
    }
  };

  /* ============================================================
     SIGN UP
  ============================================================ */

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    try {
      const response = await api.post("/signup", {
        agency_name: agencyName,
        email,
        password,
      });

      console.log("Sign Up Response:", response.data);

      dispatch(setUserEmail(email));

      // We already know the agency name because the user entered it
      dispatch(setAgencyName(agencyName));

      // Store token if signup returns one
      if (response.data.token) {
        dispatch(setUserToken(response.data.token));
      }

      navigate("/home");
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Something went wrong";

      setErrorMessage(msg);

      console.error("Sign up failed:", err);
    }
  };

  /* ============================================================
     PAGE
  ============================================================ */

  return (
    <div className="landing-page">
      <h1>Welcome to BaytCare Maids Services</h1>

      <div className="auth-forms">
        {isSignUp ? (
          <div className="sign-up">
            <h2>Sign Up</h2>

            <form onSubmit={handleSignUp}>
              <div className="form-group">
                <label htmlFor="sign-up-agency-name">Agency Name</label>

                <input
                  type="text"
                  id="sign-up-agency-name"
                  placeholder="Enter your agency name"
                  value={agencyName}
                  onChange={(e) => setAgencyNameInput(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="sign-up-email">Email</label>

                <input
                  type="email"
                  id="sign-up-email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="sign-up-password">Password</label>

                <input
                  type="password"
                  id="sign-up-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button type="submit">Sign Up</button>
            </form>

            <p>
              Already have an account?{" "}
              <button type="button" onClick={() => setIsSignUp(false)}>
                Sign In
              </button>
            </p>
          </div>
        ) : (
          <div className="sign-in">
            <h2>Sign In</h2>

            <form onSubmit={handleSignIn}>
              <div className="form-group">
                <label htmlFor="sign-in-email">Email</label>

                <input
                  type="email"
                  id="sign-in-email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="sign-in-password">Password</label>

                <input
                  type="password"
                  id="sign-in-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button type="submit">Sign In</button>
            </form>

            <p>
              Don't have an account?{" "}
              <button type="button" onClick={() => setIsSignUp(true)}>
                Sign Up
              </button>
            </p>
          </div>
        )}
      </div>

      {errorMessage && <p className="error-message">{errorMessage}</p>}
    </div>
  );
};

export default LandingPage;
